import * as React from 'react';
import { Form } from 'antd';
import { NodeBaseModel } from '../models';
import { InstanceUtils, FormUtils } from '../misc';
import { NodeFormValues } from '../types';
import { useNodeSubscriptions, useNodeFormValues } from '.';
import { useAppStore } from '../stores';
import { InputUtils } from '../misc';
import { InputBaseModel } from '../models';

export default (newNode?: NodeBaseModel, previousNode?: NodeBaseModel) => {
    const { inputsStore } = useAppStore();

    const [formKey, setFormKey] = React.useState(0);

    const [form] = Form.useForm<NodeFormValues>();

    const isInitialLoad = React.useRef(true);

    const setNewNodeFormFieldValue = React.useCallback(
        (comparisonInput: InputBaseModel) => {
            FormUtils.applyInputValue(form, comparisonInput);

            const contractInput = inputsStore.inputs.find(i => i.guid === comparisonInput.guid);

            // If contract input is not found, it means that the input is new and has unsaved changes, otherwise compare the values
            comparisonInput.setHasUnsavedChanges(
                contractInput
                    ? !InputUtils.areValuesEqual(contractInput.dataType, contractInput.value, comparisonInput.value)
                    : true
            );
        },
        [form, inputsStore]
    );

    const setPreviousNodeFormFieldValue = React.useCallback(
        (input: InputBaseModel) => FormUtils.applyInputValue(form, input),
        [form]
    );

    useNodeFormValues(newNode, setNewNodeFormFieldValue);
    useNodeFormValues(previousNode, setPreviousNodeFormFieldValue);
    useNodeSubscriptions(form, newNode, setNewNodeFormFieldValue);

    const handleInputValuesChange = (node: NodeBaseModel, inputGuid: string) => {
        const input = node.getInputByGuid(inputGuid);

        if (!input) {
            return;
        }

        const value = form.getFieldValue(inputGuid);

        input.setValue(value);

        if (InstanceUtils.isInputWithSource(input.childInput)) {
            input.childInput.fetchSourceOptionsHierarchy();
        }
    };

    const onValuesChange = (changedValues: NodeFormValues) => {
        if (!newNode) {
            return;
        }

        Object.keys(changedValues).forEach(inputGuid => handleInputValuesChange(newNode, inputGuid));

        if (newNode.hasError && newNode.isValid) {
            newNode.setHasError(false);
        }
    };

    React.useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        setFormKey(prevKey => prevKey + 1);
    }, [newNode]);

    return {
        form,
        formKey,
        onValuesChange
    };
};
